import React from "react";
import { Link } from "react-router-dom";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12 pt-24 pb-20">
        <Link to="/" className="inline-flex items-center gap-2 text-xs text-[#52525B] hover:text-[#0A0A0A] mb-8 transition-colors">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 3L5 7L9 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Back to home
        </Link>

        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-[#0A0A0A] mb-8">
          Privacy Policy
        </h1>
        <p className="text-sm text-[#52525B] mb-10">Last updated: 2026-06-01</p>

        <div className="space-y-10 text-sm text-[#0A0A0A] leading-relaxed">
          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">1. Introduction</h2>
            <p className="text-[#52525B] mb-3">
              KnowVex ("we," "us," or "our") operates the KnowVex platform and associated services (collectively, the "Services") available at knowvex.org and related domains. This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our Services.
            </p>
            <p className="text-[#52525B] mb-3">
              We are committed to protecting your privacy and handling your personal information with care and transparency. By using the Services, you agree to the collection and use of information in accordance with this Privacy Policy.
            </p>
            <p className="text-[#52525B]">
              If you have questions about this Privacy Policy, please contact us at support@knowvex.org.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">2. Information We Collect</h2>
            <p className="text-[#52525B] mb-4">We collect several types of information from and about users of our Services:</p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">2.1 Account Information</h3>
            <p className="text-[#52525B] mb-2">When you create an account, we collect:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Name</strong> &mdash; As provided by you or your authentication provider</li>
              <li><strong className="text-[#0A0A0A]">Email address</strong> &mdash; For account identification and communication</li>
              <li><strong className="text-[#0A0A0A]">Profile picture</strong> &mdash; If provided through your authentication provider (e.g., Google)</li>
              <li><strong className="text-[#0A0A0A]">Authentication credentials</strong> &mdash; OAuth tokens, session tokens, and related authentication data</li>
              <li><strong className="text-[#0A0A0A]">User ID</strong> &mdash; A unique identifier assigned to your account</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">2.2 Usage Information</h3>
            <p className="text-[#52525B] mb-2">When you use the Services, we automatically collect:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Activity logs</strong> &mdash; Actions you take within the Services, features used, and interaction patterns</li>
              <li><strong className="text-[#0A0A0A]">Service-specific data</strong> &mdash; Data related to specific tools and features you use (varies by service)</li>
              <li><strong className="text-[#0A0A0A]">Session information</strong> &mdash; Login/logout times, session duration, and authentication events</li>
              <li><strong className="text-[#0A0A0A]">Performance data</strong> &mdash; Response times, error logs, and service availability metrics</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">2.3 Technical Information</h3>
            <p className="text-[#52525B] mb-2">We automatically collect technical data from your device and browser:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">IP address</strong> &mdash; For security, fraud prevention, and geolocation</li>
              <li><strong className="text-[#0A0A0A]">Browser type and version</strong> &mdash; To optimize service delivery</li>
              <li><strong className="text-[#0A0A0A]">Device information</strong> &mdash; Operating system, device type, screen resolution</li>
              <li><strong className="text-[#0A0A0A]">Referral source</strong> &mdash; How you arrived at our Services</li>
              <li><strong className="text-[#0A0A0A]">Cookies and similar technologies</strong> &mdash; See Section 8 for details</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">2.4 Uploaded Content and User-Generated Data</h3>
            <p className="text-[#52525B] mb-2">Depending on the Services you use, we may collect:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Files and documents</strong> &mdash; Content you upload to the Services</li>
              <li><strong className="text-[#0A0A0A]">Text input</strong> &mdash; Information you enter into forms, fields, or text areas</li>
              <li><strong className="text-[#0A0A0A]">Generated outputs</strong> &mdash; Results, reports, or content generated by the Services based on your inputs</li>
              <li><strong className="text-[#0A0A0A]">Preferences and settings</strong> &mdash; Your configuration choices and customization options</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">2.5 Communication Data</h3>
            <p className="text-[#52525B] mb-2">When you contact us, we collect:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Support inquiries</strong> &mdash; Questions, issues, or feedback you submit</li>
              <li><strong className="text-[#0A0A0A]">Email correspondence</strong> &mdash; Communications between you and our team</li>
              <li><strong className="text-[#0A0A0A]">Survey responses</strong> &mdash; Feedback provided through surveys or questionnaires</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">2.6 Third-Party Information</h3>
            <p className="text-[#52525B] mb-2">We may receive information about you from third-party services you connect to our platform:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5">
              <li><strong className="text-[#0A0A0A]">OAuth providers</strong> &mdash; Information from Google, Microsoft, or other authentication services</li>
              <li><strong className="text-[#0A0A0A]">Integration partners</strong> &mdash; Data from services you authorize to connect with our platform</li>
              <li><strong className="text-[#0A0A0A]">Payment processors</strong> &mdash; Transaction and payment information (we do not store full credit card numbers)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">3. How We Use Your Information</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">3.1 Service Delivery</h3>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Provide and operate the Services &mdash; Process your requests, deliver features, and maintain functionality</li>
              <li>Authenticate your identity &mdash; Verify your account and manage access</li>
              <li>Process transactions &mdash; Handle payments and billing for paid services</li>
              <li>Generate outputs &mdash; Use your inputs to generate AI-powered results and recommendations</li>
              <li>Store and manage your data &mdash; Maintain your account data, settings, and uploaded content</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">3.2 Service Improvement</h3>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Analyze usage patterns &mdash; Understand how users interact with the Services</li>
              <li>Improve features and performance &mdash; Enhance user experience and optimize functionality</li>
              <li>Develop new services &mdash; Create new tools and features based on user needs</li>
              <li>Train AI models &mdash; Use de-identified, aggregated data to improve artificial intelligence algorithms</li>
              <li>Conduct research &mdash; Perform internal research and analytics</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">3.3 Communication</h3>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Send service notifications &mdash; Alert you about account activity, updates, and important changes</li>
              <li>Provide customer support &mdash; Respond to your inquiries and resolve issues</li>
              <li>Send product updates &mdash; Inform you about new features, services, and improvements</li>
              <li>Request feedback &mdash; Ask for your input on our Services (you can opt out of marketing communications)</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">3.4 Security and Compliance</h3>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Detect and prevent fraud &mdash; Identify suspicious activity and protect against abuse</li>
              <li>Enforce our Terms of Service &mdash; Monitor compliance with our policies</li>
              <li>Protect our rights &mdash; Defend against legal claims and protect our intellectual property</li>
              <li>Comply with legal obligations &mdash; Meet regulatory requirements and respond to lawful requests</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">3.5 Analytics and Marketing</h3>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5">
              <li>Measure Service effectiveness &mdash; Track adoption, retention, and engagement metrics</li>
              <li>Perform A/B testing &mdash; Test new features and improvements</li>
              <li>Understand user demographics &mdash; Analyze user segments and preferences (using aggregated, non-personally identifiable data)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">4. How We Share Your Information</h2>
            <p className="text-[#52525B] mb-4">We do not sell your personal information to third parties. We share your information only in the following limited circumstances:</p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">4.1 Service Providers</h3>
            <p className="text-[#52525B] mb-2">We share information with third-party service providers who perform services on our behalf, including:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Cloud infrastructure providers &mdash; AWS, Google Cloud, or similar services for hosting and storage</li>
              <li>Database services &mdash; MongoDB Atlas or similar services for data management</li>
              <li>Authentication services &mdash; Google OAuth or similar identity providers</li>
              <li>Artificial intelligence providers &mdash; Google Gemini, OpenAI, or similar AI services for content generation</li>
              <li>Payment processors &mdash; Stripe, PayPal, or similar services for payment processing</li>
              <li>Email services &mdash; SendGrid, Amazon SES, or similar services for transactional emails</li>
              <li>Analytics providers &mdash; Services that help us understand usage patterns</li>
            </ul>
            <p className="text-[#52525B] mb-3">These service providers are contractually obligated to protect your information and use it only for the purposes we specify.</p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">4.2 Legal Requirements</h3>
            <p className="text-[#52525B] mb-2">We may disclose your information when required by law or in response to:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Legal process &mdash; Court orders, subpoenas, or other legal requests</li>
              <li>Government requests &mdash; Lawful requests from law enforcement or regulatory authorities</li>
              <li>Legal rights protection &mdash; When necessary to protect our rights, property, or safety, or the rights of others</li>
              <li>Compliance obligations &mdash; To comply with applicable laws and regulations</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">4.3 Business Transfers</h3>
            <p className="text-[#52525B] mb-3">
              If we are involved in a merger, acquisition, reorganization, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change in ownership or control of your personal information.
            </p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">4.4 With Your Consent</h3>
            <p className="text-[#52525B] mb-2">We may share your information with third parties when you explicitly consent to such sharing, such as:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Integrations &mdash; When you authorize third-party applications to access your data</li>
              <li>Collaboration &mdash; When you share content or outputs with other users</li>
              <li>Public features &mdash; When you post content to public areas of the Services (if applicable)</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">4.5 Aggregated and De-Identified Data</h3>
            <p className="text-[#52525B] mb-2">We may share aggregated, de-identified, or anonymized data that cannot reasonably be used to identify you. This data may be used for:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5">
              <li>Industry research &mdash; Contributing to academic or industry studies</li>
              <li>Public reporting &mdash; Sharing usage statistics and trends</li>
              <li>Service improvement &mdash; Benchmarking and performance analysis</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">5. Data Security</h2>
            <p className="text-[#52525B] mb-4">We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">5.1 Security Measures</h3>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Encryption</strong> &mdash; Data is encrypted in transit (HTTPS/TLS) and at rest where appropriate</li>
              <li><strong className="text-[#0A0A0A]">Access controls</strong> &mdash; Role-based access restrictions and authentication requirements</li>
              <li><strong className="text-[#0A0A0A]">Secure infrastructure</strong> &mdash; Industry-standard cloud providers with security certifications</li>
              <li><strong className="text-[#0A0A0A]">Regular monitoring</strong> &mdash; Continuous monitoring for security threats and vulnerabilities</li>
              <li><strong className="text-[#0A0A0A]">Security testing</strong> &mdash; Periodic security audits and penetration testing</li>
              <li><strong className="text-[#0A0A0A]">Incident response</strong> &mdash; Procedures for detecting, responding to, and recovering from security incidents</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">5.2 Third-Party Security</h3>
            <p className="text-[#52525B] mb-3">
              Our third-party service providers are selected based on their security practices and compliance with industry standards. However, we cannot guarantee the security of information transmitted to or from third-party services.
            </p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">5.3 Your Responsibility</h3>
            <p className="text-[#52525B] mb-2">You are responsible for:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Maintaining account security</strong> &mdash; Keeping your login credentials confidential</li>
              <li><strong className="text-[#0A0A0A]">Monitoring account activity</strong> &mdash; Reviewing your account for unauthorized access</li>
              <li><strong className="text-[#0A0A0A]">Reporting security issues</strong> &mdash; Notifying us immediately of any suspected security breaches</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">5.4 Data Breach Notification</h3>
            <p className="text-[#52525B] mb-3">
              In the event of a data breach that affects your personal information, we will notify you as required by applicable law. Notifications will be sent to the email address associated with your account.
            </p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">5.5 Limitations</h3>
            <p className="text-[#52525B]">
              While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security of your information.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">6. Data Retention</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">6.1 Retention Periods</h3>
            <p className="text-[#52525B] mb-2">We retain your personal information for as long as necessary to:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Provide the Services &mdash; While your account is active</li>
              <li>Comply with legal obligations &mdash; As required by applicable laws</li>
              <li>Resolve disputes &mdash; To address claims or litigation</li>
              <li>Enforce our agreements &mdash; To uphold our Terms of Service</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">6.2 Account Deletion</h3>
            <p className="text-[#52525B] mb-2">When you delete your account:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Active data deletion</strong> &mdash; We will delete or anonymize your personal information within a reasonable timeframe (typically 30-90 days)</li>
              <li><strong className="text-[#0A0A0A]">Backup retention</strong> &mdash; Information in backups may persist for a limited period (typically up to 90 days)</li>
              <li><strong className="text-[#0A0A0A]">Legal retention</strong> &mdash; Some data may be retained longer if required by law or for legitimate business purposes</li>
              <li><strong className="text-[#0A0A0A]">Shared content</strong> &mdash; Content you shared with other users may remain available to those users</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">6.3 Data Export</h3>
            <p className="text-[#52525B]">
              Before deleting your account, you may request an export of your data. Contact us at support@knowvex.org to request a data export.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">7. Your Rights and Choices</h2>
            <p className="text-[#52525B] mb-4">Depending on your location, you may have certain rights regarding your personal information:</p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">7.1 Access and Portability</h3>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Access your data</strong> &mdash; Request a copy of the personal information we hold about you</li>
              <li><strong className="text-[#0A0A0A]">Data portability</strong> &mdash; Receive your data in a structured, machine-readable format</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">7.2 Correction and Deletion</h3>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Correct inaccuracies</strong> &mdash; Update or correct your personal information through account settings</li>
              <li><strong className="text-[#0A0A0A]">Delete your data</strong> &mdash; Request deletion of your personal information (subject to legal retention requirements)</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">7.3 Opt-Out and Restrictions</h3>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Marketing communications</strong> &mdash; Opt out of promotional emails by clicking "unsubscribe" in any marketing email</li>
              <li><strong className="text-[#0A0A0A]">Cookie preferences</strong> &mdash; Manage cookies through your browser settings (see Section 8)</li>
              <li><strong className="text-[#0A0A0A]">Limit data processing</strong> &mdash; Request restrictions on how we process your data (where legally applicable)</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">7.4 Withdrawal of Consent</h3>
            <p className="text-[#52525B] mb-3">
              Where we rely on your consent to process personal information, you may withdraw that consent at any time. Withdrawal will not affect the lawfulness of processing based on consent before its withdrawal.
            </p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">7.5 Object to Processing</h3>
            <p className="text-[#52525B] mb-3">
              You may object to certain types of processing, such as processing for direct marketing purposes.
            </p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">7.6 Automated Decision-Making</h3>
            <p className="text-[#52525B] mb-2">If we use automated decision-making that significantly affects you, you have the right to:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Be informed</strong> &mdash; Know when automated decisions are being made</li>
              <li><strong className="text-[#0A0A0A]">Human review</strong> &mdash; Request human review of automated decisions (where applicable)</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">7.7 Exercising Your Rights</h3>
            <p className="text-[#52525B]">
              To exercise any of these rights, please contact us at support@knowvex.org. We will respond to your request within a reasonable timeframe as required by applicable law (typically 30 days). We may ask you to verify your identity before processing your request.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">8. Cookies and Tracking Technologies</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">8.1 What Are Cookies</h3>
            <p className="text-[#52525B] mb-2">
              Cookies are small text files stored on your device by your web browser. We use cookies and similar technologies to:
            </p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Maintain your session &mdash; Keep you logged in across pages</li>
              <li>Remember preferences &mdash; Store your settings and customization choices</li>
              <li>Analyze usage &mdash; Understand how you interact with the Services</li>
              <li>Improve performance &mdash; Optimize page load times and functionality</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">8.2 Types of Cookies We Use</h3>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Essential cookies</strong> &mdash; Required for the Services to function (e.g., session management, authentication)</li>
              <li><strong className="text-[#0A0A0A]">Functional cookies</strong> &mdash; Enhance your experience (e.g., remember language preferences)</li>
              <li><strong className="text-[#0A0A0A]">Analytics cookies</strong> &mdash; Help us understand usage patterns and improve the Services</li>
              <li><strong className="text-[#0A0A0A]">Performance cookies</strong> &mdash; Monitor and improve Service performance</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">8.3 Third-Party Cookies</h3>
            <p className="text-[#52525B] mb-3">
              Third-party service providers may set cookies through the Services for analytics, advertising, or other purposes. We do not control these third-party cookies.
            </p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">8.4 Managing Cookies</h3>
            <p className="text-[#52525B] mb-2">You can control cookies through your browser settings:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Block cookies</strong> &mdash; Configure your browser to refuse all cookies</li>
              <li><strong className="text-[#0A0A0A]">Delete cookies</strong> &mdash; Remove cookies already stored on your device</li>
              <li><strong className="text-[#0A0A0A]">Cookie preferences</strong> &mdash; Accept some cookies while blocking others</li>
            </ul>
            <p className="text-[#52525B] mb-3">
              Note that disabling essential cookies may affect your ability to use certain features of the Services.
            </p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">8.5 Do Not Track</h3>
            <p className="text-[#52525B]">
              Some browsers offer a "Do Not Track" (DNT) signal. We do not currently respond to DNT signals, as there is no industry standard for how to interpret and implement them.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">9. Third-Party Links and Services</h2>
            <p className="text-[#52525B] mb-4">The Services may contain links to third-party websites, applications, or services that we do not control. This Privacy Policy applies only to our Services.</p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">9.1 Third-Party Privacy Practices</h3>
            <p className="text-[#52525B] mb-3">
              We are not responsible for the privacy practices of third-party services. We encourage you to review the privacy policies of any third-party services you access through our platform.
            </p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">9.2 Third-Party Integrations</h3>
            <p className="text-[#52525B]">
              If you connect third-party services to your account, those services may access your information as authorized by you. Review the permissions carefully before connecting third-party services.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">10. Children's Privacy</h2>
            <p className="text-[#52525B] mb-3">
              The Services are not intended for individuals under the age of 13 (or the applicable age of digital consent in your jurisdiction). We do not knowingly collect personal information from children.
            </p>
            <p className="text-[#52525B] mb-3">
              If we learn that we have collected personal information from a child without parental consent, we will take steps to delete that information promptly.
            </p>
            <p className="text-[#52525B]">
              If you believe we have collected information from a child, please contact us at support@knowvex.org.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">11. International Data Transfers</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">11.1 Cross-Border Transfers</h3>
            <p className="text-[#52525B] mb-3">
              The Services are operated from India. If you access the Services from outside this jurisdiction, your information may be transferred to, stored in, and processed in India or other countries where our service providers operate.
            </p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">11.2 Data Protection Standards</h3>
            <p className="text-[#52525B] mb-2">When we transfer personal information internationally, we implement appropriate safeguards, such as:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li><strong className="text-[#0A0A0A]">Standard contractual clauses</strong> &mdash; Approved by relevant data protection authorities</li>
              <li><strong className="text-[#0A0A0A]">Service provider agreements</strong> &mdash; Contracts requiring third parties to protect your information</li>
              <li><strong className="text-[#0A0A0A]">Compliance with local laws</strong> &mdash; Adherence to applicable data protection regulations</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">11.3 Your Consent</h3>
            <p className="text-[#52525B]">
              By using the Services, you consent to the transfer of your information to countries outside your country of residence, which may have different data protection laws.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">12. Changes to This Privacy Policy</h2>
            <p className="text-[#52525B] mb-4">We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or Services.</p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">12.1 Notification of Changes</h3>
            <p className="text-[#52525B] mb-2">We will notify you of material changes by:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Updating the "Last Updated" date &mdash; At the top of this Privacy Policy</li>
              <li>Posting a notice &mdash; On our website or within the Services</li>
              <li>Sending an email &mdash; To the email address associated with your account (for significant changes)</li>
            </ul>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">12.2 Continued Use</h3>
            <p className="text-[#52525B] mb-3">
              Your continued use of the Services after the effective date of a revised Privacy Policy constitutes your acceptance of the changes. If you do not agree to the updated Privacy Policy, you should discontinue use of the Services and delete your account.
            </p>

            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">12.3 Review Regularly</h3>
            <p className="text-[#52525B]">
              We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">13. Contact Us</h2>
            <p className="text-[#52525B] mb-3">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="text-[#52525B] border border-black/10 p-4">
              <p className="font-semibold text-[#0A0A0A]">KnowVex</p>
              <p>Email: support@knowvex.org</p>
              <p>Address: D-111, Sector 26, Noida, Uttar Pradesh, India 201301</p>
            </div>
            <p className="text-[#52525B] mt-3 text-xs italic">
              Your privacy matters to us. Thank you for trusting KnowVex with your information.
            </p>
          </section>
        </div>
      </div>

      <footer className="border-t border-black/10 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo_vector.svg" alt="Quick Assess" className="w-8 h-8" />
            <div className="flex flex-col leading-tight">
              <span className="text-base text-[#52525B]">Quick <span className="text-[#FF3366]">Assess</span></span>
              <span className="text-xs text-[#52525B]">by KnowVex</span>
            </div>
          </div>
          <p className="text-xs text-[#52525B]">AI-powered technical assessments</p>
        </div>
      </footer>
    </div>
  );
}
